import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Package, AlertCircle, Music, Guitar, Users, Star, TrendingUp } from 'lucide-react';
import { ProductInterface } from '@/types/product';
import ProductWrapperCard from '../ui/product/product-wrapper-card';

export interface ApiResponseProductScroll {
  products: ProductInterface[];
  hasMore: boolean;
  nextPage?: number;
  total?: number;
}

interface ScrollToLoadProductsProps {
  fetchProducts: (page: number, limit: number) => Promise<ApiResponseProductScroll>;
  itemsPerPage?: number;
  loadingComponent?: React.ReactNode;
  errorComponent?: (error: string, retry: () => void) => React.ReactNode;
  renderProduct?: (product: ProductInterface, index: number) => React.ReactNode;
  containerClassName?: string;
  gridClassName?: string;
  loadOffset?: number;
  onProductsLoaded?: (products: ProductInterface[], totalLoaded: number) => void;
  onError?: (error: string) => void;
  categoryName?: string;
}

export const ScrollToLoadProductsWithCategory: React.FC<ScrollToLoadProductsProps> = ({
  fetchProducts,
  itemsPerPage = 8,
  loadingComponent,
  errorComponent,
  renderProduct,
  containerClassName = '',
  gridClassName = '',
  loadOffset = 100,
  categoryName = "Guitar",
  onProductsLoaded,
  onError,
}) => {
  const [products, setProducts] = useState<ProductInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [initialLoad, setInitialLoad] = useState(true);

  const observerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const hasLoadedInitialRef = useRef(false);
  const productsRef = useRef<ProductInterface[]>([]); // ✅ Keep products in ref to avoid stale closures

  // ✅ Sync products ref with state
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  // ✅ Stable loadProducts function - no products.length dependency
  const loadProducts = useCallback(async (page: number, reset = false) => {
    if (loadingRef.current) {
      console.log('⏭️ Skipping - already loading');
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // console.log('📡 Fetching products:', { page, reset });
      const response = await fetchProducts(page, itemsPerPage);
      
      setProducts((prev) => {
        const newProducts = reset ? response.products : [...prev, ...response.products];
        
        // Use the ref to get current length for callback
        const totalLoaded = newProducts.length;
        onProductsLoaded?.(response.products, totalLoaded);
        
        return newProducts;
      });
      
      setHasMore(response.hasMore);
      setCurrentPage(page);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Không thể tải sản phẩm guitar';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
      loadingRef.current = false;
      setInitialLoad(false);
    }
  }, [fetchProducts, itemsPerPage, onProductsLoaded, onError]); // ✅ Removed products.length

  // ✅ Load initial products only once
  useEffect(() => {
    if (!hasLoadedInitialRef.current) {
      console.log('🎬 Initial load');
      hasLoadedInitialRef.current = true;
      loadProducts(1, true);
    }
  }, [loadProducts]);

  // ✅ Intersection Observer for infinite scroll
  useEffect(() => {
    if (initialLoad) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !loading && hasMore && !error) {
          console.log('👁️ Observer triggered - loading next page');
          loadProducts(currentPage + 1);
        }
      },
      {
        threshold: 0.1,
        rootMargin: `${loadOffset}px`,
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [initialLoad, loading, hasMore, error, currentPage, loadOffset, loadProducts]);

  const retry = useCallback(() => {
    if (productsRef.current.length === 0) {
      loadProducts(1, true);
    } else {
      loadProducts(currentPage + 1);
    }
  }, [loadProducts, currentPage]);

  const defaultLoadingComponent = (
    <div className="flex items-center justify-center py-12">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
          <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-[var(--color-primary-light)] animate-pulse"></div>
        </div>
        <div className="flex items-center gap-2">
          <Music className="w-4 h-4 text-[var(--color-text-muted)]" />
          <span className="text-[var(--color-text-muted)] font-medium">Đang tải guitar...</span>
        </div>
      </div>
    </div>
  );

  const defaultErrorComponent = (error: string, retryFn: () => void) => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="bg-[var(--color-accent-red-light)] rounded-full p-4 mb-6">
        <AlertCircle className="w-8 h-8 text-[var(--color-accent-red)]" />
      </div>
      <h3 className="text-xl font-semibold text-[var(--color-text-primary)] mb-2">
        Lỗi Tải Sản Phẩm
      </h3>
      <p className="text-[var(--color-text-muted)] mb-6 text-center max-w-md">
        {error}
      </p>
      <button
        onClick={retryFn}
        className="inline-flex items-center gap-2 bg-[var(--gradient-primary)] text-[var(--color-text-white)] px-6 py-3 rounded-xl hover:shadow-[var(--shadow-hover)] transition-all duration-300 font-semibold">
        <Package className="w-4 h-4" />
        Thử Lại
      </button>
    </div>
  );

  if (initialLoad && loading) {
    return (
      <div className={`w-full ${containerClassName}`}>
        {loadingComponent || defaultLoadingComponent}
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className={`w-full ${containerClassName}`}>
        {errorComponent
          ? errorComponent(error, retry)
          : defaultErrorComponent(error, retry)}
      </div>
    );
  }

  return (
    <div className={`w-full ${containerClassName} mt-[30px]`}>
      {/* Header */}
      <div className="text-center mb-2 md:mb-16">
        <div className="inline-flex items-center justify-center space-x-2 bg-[var(--color-bg-accent)] text-[var(--color-primary)] px-6 py-3 rounded-full text-sm font-semibold mb-8 border border-[var(--color-border-light)]">
          <Guitar className="w-4 h-4" />
          <span>Guitar Thuộc Danh Mục ({categoryName})</span>
        </div>

        <p className="hidden md:block text-base sm:text-lg text-[var(--color-text-secondary)] max-w-2xl mx-auto leading-relaxed mb-8">
          Khám phá bộ sưu tập guitar được lựa chọn kỹ lưỡng — từ guitar acoustic cổ điển đến guitar điện hiện đại, phù hợp với mọi phong cách âm nhạc.
        </p>

        <div className="hidden md:flex flex-wrap justify-center gap-8 mb-12">
          <div className="flex items-center space-x-3 text-[var(--color-text-muted)]">
            <div className="bg-[var(--color-accent-green-light)] p-3 rounded-full">
              <TrendingUp className="w-5 h-5 text-[var(--color-accent-green)]" />
            </div>
            <div>
              <div className="text-xl font-bold text-[var(--color-text-primary)]">200+</div>
              <div className="text-sm">Guitar bán chạy</div>
            </div>
          </div>

          <div className="flex items-center space-x-3 text-[var(--color-text-muted)]">
            <div className="bg-[var(--color-bg-accent)] p-3 rounded-full">
              <Star className="w-5 h-5 text-[var(--color-primary)]" />
            </div>
            <div>
              <div className="text-xl font-bold text-[var(--color-text-primary)]">4.9★</div>
              <div className="text-sm">Đánh giá trung bình</div>
            </div>
          </div>

          <div className="flex items-center space-x-3 text-[var(--color-text-muted)]">
            <div className="bg-[var(--gradient-accent)] p-3 rounded-full">
              <Users className="w-5 h-5 text-[var(--color-primary)]" />
            </div>
            <div>
              <div className="text-xl font-bold text-[var(--color-text-primary)]">5000+</div>
              <div className="text-sm">Khách hàng hài lòng</div>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <ProductWrapperCard hiddenGridButton={true} products={products} />

      {/* Loading More */}
      {loading && products.length > 0 && (
        <div className="mt-12">
          {loadingComponent || defaultLoadingComponent}
        </div>
      )}

      {/* Error While Loading More */}
      {error && products.length > 0 && (
        <div className="mt-12">
          {errorComponent
            ? errorComponent(error, retry)
            : defaultErrorComponent(error, retry)}
        </div>
      )}

      {/* No More Products */}
      {!hasMore && products.length > 0 && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="bg-[var(--color-bg-secondary)] rounded-full p-4 mb-4">
            <Music className="w-6 h-6 text-[var(--color-primary)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">
            Đã Xem Hết 
          </h3>
          <p className="text-[var(--color-text-muted)] text-center max-w-md">
            Bạn đã xem hết tất cả guitar trong danh mục này. Hãy khám phá các danh mục khác!
          </p>
        </div>
      )}

      {/* Observer Target */}
      {hasMore && !error && (
        <div ref={observerRef} className="h-4" />
      )}
    </div>
  );
};